/**
 * Performance monitoring utilities for tracking Web Vitals, bundle size,
 * render performance, and storage usage.
 */

import React from 'react';

export interface WebVitals {
  lcp?: number; // Largest Contentful Paint
  fid?: number; // First Input Delay
  cls?: number; // Cumulative Layout Shift
  fcp?: number; // First Contentful Paint
  ttfb?: number; // Time to First Byte
}

export interface PerformanceMetrics {
  webVitals: WebVitals;
  bundleSize?: number;
  renderTime?: number;
  storageUsage?: {
    used: number;
    quota: number;
    percentage: number;
  };
  timestamp: number;
}

/**
 * Tracks Web Vitals using the Web Vitals library pattern
 * Can be extended to use the actual web-vitals library if needed
 */
export class PerformanceMonitor {
  private metrics: PerformanceMetrics[] = [];
  private maxMetrics = 100; // Keep last 100 metrics

  /**
   * Initialize Web Vitals tracking
   */
  init() {
    if (typeof window === 'undefined') return;

    // Track LCP (Largest Contentful Paint)
    this.trackLCP();

    // Track FID (First Input Delay) - now called INP in newer APIs
    this.trackFID();

    // Track CLS (Cumulative Layout Shift)
    this.trackCLS();

    // Track FCP (First Contentful Paint)
    this.trackFCP();

    // Track TTFB (Time to First Byte)
    this.trackTTFB();
  }

  /**
   * Track Largest Contentful Paint (LCP)
   */
  private trackLCP() {
    if (!('PerformanceObserver' in window)) return;

    try {
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const lastEntry = entries[entries.length - 1] as any;
        
        if (lastEntry) {
          this.recordMetric('lcp', lastEntry.renderTime || lastEntry.loadTime);
        }
      });

      observer.observe({ entryTypes: ['largest-contentful-paint'] });
    } catch (e) {
      console.warn('LCP tracking not supported:', e);
    }
  }

  /**
   * Track First Input Delay (FID) / Interaction to Next Paint (INP)
   */
  private trackFID() {
    if (!('PerformanceObserver' in window)) return;

    try {
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach((entry: any) => {
          if (entry.processingStart && entry.startTime) {
            const delay = entry.processingStart - entry.startTime;
            this.recordMetric('fid', delay);
          }
        });
      });

      observer.observe({ entryTypes: ['first-input', 'event'] });
    } catch (e) {
      console.warn('FID tracking not supported:', e);
    }
  }

  /**
   * Track Cumulative Layout Shift (CLS)
   */
  private trackCLS() {
    if (!('PerformanceObserver' in window)) return;

    try {
      let clsValue = 0;
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach((entry: any) => {
          if (!entry.hadRecentInput) {
            clsValue += entry.value;
          }
        });
        this.recordMetric('cls', clsValue);
      });

      observer.observe({ entryTypes: ['layout-shift'] });
    } catch (e) {
      console.warn('CLS tracking not supported:', e);
    }
  }

  /**
   * Track First Contentful Paint (FCP)
   */
  private trackFCP() {
    if (!('PerformanceObserver' in window)) return;

    try {
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach((entry) => {
          if (entry.name === 'first-contentful-paint') {
            this.recordMetric('fcp', entry.startTime);
          }
        });
      });

      observer.observe({ entryTypes: ['paint'] });
    } catch (e) {
      console.warn('FCP tracking not supported:', e);
    }
  }

  /**
   * Track Time to First Byte (TTFB)
   */
  private trackTTFB() {
    if (!('performance' in window) || !('timing' in window.performance)) return;

    try {
      const timing = window.performance.timing;
      const ttfb = timing.responseStart - timing.requestStart;
      this.recordMetric('ttfb', ttfb);
    } catch (e) {
      console.warn('TTFB tracking not supported:', e);
    }
  }

  /**
   * Record a performance metric
   */
  private recordMetric(key: keyof WebVitals, value: number) {
    const latest = this.getLatestMetrics();
    latest.webVitals[key] = value;
    this.updateLatestMetrics(latest);
  }

  /**
   * Get the latest performance metrics
   */
  private getLatestMetrics(): PerformanceMetrics {
    const latest = this.metrics[this.metrics.length - 1];
    if (latest && Date.now() - latest.timestamp < 1000) {
      return latest;
    }
    return {
      webVitals: {},
      timestamp: Date.now(),
    };
  }

  /**
   * Update the latest metrics
   */
  private updateLatestMetrics(metrics: PerformanceMetrics) {
    const lastIndex = this.metrics.length - 1;
    if (lastIndex >= 0 && Date.now() - this.metrics[lastIndex].timestamp < 1000) {
      this.metrics[lastIndex] = metrics;
    } else {
      this.metrics.push(metrics);
      if (this.metrics.length > this.maxMetrics) {
        this.metrics.shift();
      }
    }
  }

  /**
   * Track bundle size (should be called after build)
   */
  trackBundleSize() {
    if (typeof window === 'undefined') return;

    try {
      // Get total size of all loaded scripts
      const scripts = Array.from(document.querySelectorAll('script[src]'));
      let totalSize = 0;

      Promise.all(
        scripts.map((script) => {
          const src = (script as HTMLScriptElement).src;
          if (!src) return Promise.resolve(0);

          return fetch(src, { method: 'HEAD' })
            .then((response) => {
              const size = parseInt(response.headers.get('content-length') || '0', 10);
              return size;
            })
            .catch(() => 0);
        })
      ).then((sizes) => {
        totalSize = sizes.reduce((sum, size) => sum + size, 0);
        const latest = this.getLatestMetrics();
        latest.bundleSize = totalSize;
        this.updateLatestMetrics(latest);
      });
    } catch (e) {
      console.warn('Bundle size tracking failed:', e);
    }
  }

  /**
   * Track component render time
   */
  trackRenderTime(componentName: string, renderTime: number) {
    if (process.env.NODE_ENV === 'development') {
      console.log(`[Performance] ${componentName} rendered in ${renderTime.toFixed(2)}ms`);
    }

    const latest = this.getLatestMetrics();
    if (!latest.renderTime || renderTime > latest.renderTime) {
      latest.renderTime = renderTime;
      this.updateLatestMetrics(latest);
    }
  }

  /**
   * Track storage usage
   */
  async trackStorageUsage(): Promise<{ used: number; quota: number; percentage: number } | null> {
    if (typeof navigator === 'undefined' || !('storage' in navigator) || !('estimate' in navigator.storage)) {
      return null;
    }

    try {
      const estimate = await navigator.storage.estimate();
      const used = estimate.usage || 0;
      const quota = estimate.quota || 0;
      const percentage = quota > 0 ? (used / quota) * 100 : 0;

      const latest = this.getLatestMetrics();
      latest.storageUsage = { used, quota, percentage };
      this.updateLatestMetrics(latest);

      // Alert if storage is getting full
      if (percentage > 80) {
        console.warn(
          `[Performance] Storage usage is at ${percentage.toFixed(1)}% (${this.formatBytes(used)} / ${this.formatBytes(quota)})`
        );
      }

      return { used, quota, percentage };
    } catch (e) {
      console.warn('Storage usage tracking failed:', e);
      return null;
    }
  }

  /**
   * Get all collected metrics
   */
  getMetrics(): PerformanceMetrics[] {
    return [...this.metrics];
  }

  /**
   * Get the latest metrics summary
   */
  getLatestSummary(): PerformanceMetrics | null {
    return this.metrics[this.metrics.length - 1] || null;
  }

  /**
   * Format bytes to human-readable string
   */
  private formatBytes(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  }

  /**
   * Clear all metrics
   */
  clear() {
    this.metrics = [];
  }
}

// Singleton instance
export const performanceMonitor = new PerformanceMonitor();

/**
 * React hook for tracking component render performance
 */
export function useRenderPerformance(componentName: string) {
  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    const startTime = performance.now();

    return () => {
      const endTime = performance.now();
      const renderTime = endTime - startTime;
      performanceMonitor.trackRenderTime(componentName, renderTime);
    };
  }, [componentName]);
}
